import { memo } from 'react';

import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose, withHandlers, withPropsOnChange, withState } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { errorBoundary, withStandardPageAndLoading } from 'src/app/hocs';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import { AccountPageAddAddress } from 'src/app/router/paths';
import { openModal, segmentTrack, setValidateAddressFlagAction, validateAddressAction } from 'src/app/state/actions';
import { getCurrentCartId } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';

import { ValidateAddress } from '.';

function makeMapStateToProps() {
  return (state: RootState) => {
    const { invalidAddress, suggestedAddress, userValidatedAddress, successAction, pushEditRoute } =
      state.addressValidation;
    const initialAddress = state?.setEditAddressState ?? null;
    const previousLocation = state.routerLocation?.previous?.pathname;
    const isFromAccountDeliveryAddress = previousLocation === AccountPageAddAddress.toPath();

    return {
      cartId: getCurrentCartId(state),
      invalidAddress,
      suggestedAddress,
      userValidatedAddress,
      successAction,
      pushEditRoute,
      initialAddress,
      isFromAccountDeliveryAddress,
    };
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchValidateAddressAction: async (formValues, successAction, pushEditRoute, prevId) =>
    await dispatch(validateAddressAction(formValues, successAction, pushEditRoute, prevId)),
  dispatchSetValidateAddressFlagAction: () => dispatch(setValidateAddressFlagAction({ userValidatedAddress: true })),
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
  dispatchOpenModal: ({ modalType, handleConfirmClick, confirmMessage }) =>
    dispatch(openModal({ modalType, handleConfirmClick, confirmMessage })),
});

const connector = connect(makeMapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

export default compose(
  errorBoundary,
  connector,
  withState('isLoading', 'setIsLoading', false),
  withState('selectedAddress', 'setSelectedAddress', null),
  withState('isSubmitting', 'setIsSubmitting', false),
  withState('errors', 'setErrors', false),
  withHandlers({
    handleEditClick:
      ({ pushEditRoute, dispatchSetValidateAddressFlagAction, dispatchSegmentTrack }: PropsFromRedux) =>
      (event, address) => {
        event.preventDefault();
        event.stopPropagation();
        dispatchSegmentTrack(UNIFIED.editDeliveryAddress, {
          zipcode: address.zip_code,
        });
        dispatchSetValidateAddressFlagAction();
        pushEditRoute();
      },
    submitAddress:
      ({
        selectedAddress,
        invalidAddress,
        suggestedAddress,
        dispatchValidateAddressAction,
        setIsSubmitting,
        successAction,
        setErrors,
        pushEditRoute,
        initialAddress,
        dispatchOpenModal,
        isFromAccountDeliveryAddress,
      }) =>
      async () => {
        const address =
          selectedAddress === 'recommended'
            ? { ...suggestedAddress, is_valid: true }
            : { ...invalidAddress, is_valid: false };
        const { id } = initialAddress || {};

        setIsSubmitting(true);
        try {
          await dispatchValidateAddressAction({ ...address }, successAction, pushEditRoute, id);
          if (isFromAccountDeliveryAddress) {
            dispatchOpenModal({
              modalType: 'ok',
              confirmMessage: 'Great! Your delivery address has been uploaded.',
            });
          }
          setIsSubmitting(false);
        } catch (error) {
          setErrors(extractValidationErrors(error));
          setIsSubmitting(false);
        }
      },
  }),
  withPropsOnChange(['invalidAddress', 'suggestedAddress'], ({ invalidAddress, suggestedAddress, setIsLoading }) => {
    if (!invalidAddress && !suggestedAddress) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }),
  withStandardPageAndLoading('Verify your address'),
  memo
)(ValidateAddress);
